import React, { useState, useEffect } from 'react';
import SiteService from '../SiteService';
import MainMeterSelector from '../MainMeterSelector';
import InvoiceSelector from '../InvoiceSelector';
import SubMeterRow from './SubMeterRow';
import { Alert, AlertTitle, Box, Button, Snackbar, Typography } from '@mui/material';
import { Modal } from 'react-bootstrap';
import { formatNumber } from '../Utils/Converters';
import { useNavigate } from 'react-router-dom';
import { Backdrop, CircularProgress } from '@mui/material';

const AllocateInvoice = () => {
    const [mainMeterId, setMainMeterId] = useState(null);
    const [invoice, setInvoice] = useState(null);
    const [subMeters, setSubMeters] = useState([]);
    const sidebarWidth = 290;
    const invoiceScreenRatio = 0.6;

    const [timelineLength, setTimelineLength] = useState((window.innerWidth - sidebarWidth) * invoiceScreenRatio);
    const [invoiceStart, setInvoiceStart] = useState(null);
    const [invoiceEnd, setInvoiceEnd] = useState(null);
    const [invoiceDuration, setInvoiceDuration] = useState(null);

    const [show, setShow] = useState(false);
    const [error, setError] = useState(false); // Hata mesajı durumunu kontrol etmek için

    const [validSelections, setValidSelections] = useState([]);
    const [selections, setSelections] = useState([]);

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Timeline uzunluğunu güncellemek için
    useEffect(() => {
        const updateTimelineLength = () => {
            setTimelineLength((window.innerWidth - sidebarWidth) * invoiceScreenRatio);
        };
        window.addEventListener('resize', updateTimelineLength);
        return () => window.removeEventListener('resize', updateTimelineLength);
    }, []);

    // Invoice verisi geldiğinde alt sayaçları al
    useEffect(() => {
        if (invoice) {
            SiteService.getSubMetersWithMeterReadingsByInvoiceId(mainMeterId, invoice.invoiceId).then((res) => {
                setSubMeters(res.data);
            });

            const start = new Date(invoice.startDate).getTime();
            const end = new Date(invoice.endDate).getTime();
            setInvoiceStart(start);
            setInvoiceEnd(end);
            setInvoiceDuration(end - start);
        }
    }, [invoice]);

    // Seçimler ve geçerli seçimleri ayarla
    useEffect(() => {
        if (subMeters.length > 0) {
            setSelections(subMeters.map(() => 0)); // Her SubMeter için başlangıç değeri 0
            setValidSelections(subMeters.map(() => false)); // Başlangıçta tüm seçimler geçersiz
        }
    }, [subMeters]);

    // Seçim değiştiğinde çağrılır
    const handleSelectionChange = (index, value) => {
        const newSelections = [...selections];
        newSelections[index] = value;
        setSelections(newSelections);
        setError(false); // Yeni seçim yapıldığında hatayı temizle
    };

    // Hata mesajı kapanınca yapılacak işlem
    const handleAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setError(false);
    };

    // Fatura işlemini başlat
    const handleSubmit = () => {
        let hasInvalidSelections = false; // Seçim yapılmamış sayaçlar için
        let hasMissingReadings = false; // Eksik sayaç okumalar için

        subMeters.forEach((subMeter, index) => {
            if (!subMeter.hasMissingMeterReading && selections[index] === 0) {
                hasInvalidSelections = true;
            }
            if (subMeter.hasMissingMeterReading) {
                hasMissingReadings = true;
            }
        });

        if (hasInvalidSelections) {
            setError(true); // Geçersiz seçim hatasını göster
            return; // İşlemi durdur
        }

        if (hasMissingReadings) {
            setShow(true); // Eksik sayaç okuma için modal göster
            return; // İşlemi durdur
        }

        // Her şey uygunsa
        handleInvoiceAllocation();
    };

    // Modal onayı alındığında fatura işlemi başlatılır
    const handleModalConfirm = () => {
        setShow(false); // Modalı kapat
        handleInvoiceAllocation(); // İşlem yap
    };

    // Faturayı paylaştırma işlemini gerçekleştir
    const handleInvoiceAllocation = async () => {

        const requestData = subMeters.map((subMeter, index) => ({
            subMeterId: subMeter.subMeterId,
            prorationType: selections[index],
        }));

        setLoading(true); // Yükleniyor durumunu başlat

        try {
            // API isteği gönder
            const response = await SiteService.allocateSubMeterConsumptions(mainMeterId, invoice.invoiceId, requestData);

            navigate(`/rating-summary/${mainMeterId}/${invoice.invoiceId}`);
        } catch (error) {
            // Hata durumunda kullanıcıyı bilgilendir
            console.error('API Hatası:', error);
            alert('Fatura paylaştırma işleminde hata oluştu.');
        } finally {
            setLoading(false); // Yükleniyor durumunu kapat
        }
    };

    // Position hesaplamayı yapacak fonksiyon
    const calculatePosition = (tmpDate) => {
        if (!invoiceDuration || invoiceDuration <= 0) return 0;
        const readingTime = new Date(tmpDate).getTime();
        const relativePosition = (readingTime - invoiceStart) / invoiceDuration;
        return Math.max(0, Math.min(relativePosition * timelineLength, timelineLength * 1.5));
    };

    // Modal kapanma fonksiyonu
    const handleClose = () => setShow(false);

    return (
        <div className="card">
            <MainMeterSelector mainMeterId={mainMeterId} setMainMeterId={setMainMeterId} />
            <h3 className="text-center">Fatura Tüketimini Süzme Sayaçlara Paylaştır</h3>

            <Box>
                <InvoiceSelector unAllocated={true} mainMeterId={mainMeterId} setSelectedInvoice={setInvoice} />
            </Box>

            {invoice ? (
                <Box sx={{ width: '100%', padding: '20px' }}>
                    <Box
                        sx={{
                            position: 'relative',
                            width: `${timelineLength}px`,
                            height: '50px',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '4px',
                            mb: 3,
                            display: 'flex',               // Flexbox kullan
                            justifyContent: 'center',      // Yatayda ortala
                            alignItems: 'center',          // Dikeyde ortala
                        }}
                    >
                        <Typography variant="body1">
                            Toplam Tüketim: {formatNumber(invoice.totalConsumption)} kWh, {formatNumber(invoice.amount)} TL
                        </Typography>

                        <Typography variant="body1" sx={{ position: 'absolute', left: 0 }}>
                            {invoice.startDate}
                        </Typography>

                        <Typography variant="body1" sx={{ position: 'absolute', right: 0 }}>
                            {invoice.endDate}
                        </Typography>

                        <Box
                            className="invoice-end-line"
                            sx={{
                                position: 'absolute',
                                left: `${timelineLength}px`, // Fatura bitiş çubuğunun sonu
                                top: 0,
                                height: `${subMeters.length * 100}px`, // Submeter sayısına göre çizgi uzunluğu
                                borderLeft: '2px dashed red',
                            }}
                        />
                    </Box>

                    {subMeters.map((subMeter, index) => (
                        <SubMeterRow
                            key={subMeter.subMeterId}
                            subMeter={subMeter}
                            calculatePosition={calculatePosition}
                            timelineLength={timelineLength}
                            selection={selections[index]}
                            onSelectionChange={(value) => handleSelectionChange(index, value)}
                        />
                    ))}

                    <Snackbar open={error} autoHideDuration={6000} onClose={handleAlertClose}>
                        <Alert
                            onClose={handleAlertClose}
                            severity="error"
                            variant="filled"
                            sx={{ width: '100%' }}
                        >
                            Tüm geç sayaç okumalar için yöntem seçiniz.
                        </Alert>
                    </Snackbar>

                    {/* Yükleme sırasında ekranı kilitleyen popup */}
                    <Backdrop
                        open={loading}
                        sx={{
                            color: '#fff',
                            zIndex: (theme) => theme.zIndex.modal + 1,
                            flexDirection: 'column'
                        }}
                    >
                        <CircularProgress color="inherit" />
                        <Typography sx={{ mt: 2 }}>Fatura paylaştırılıyor...</Typography>
                    </Backdrop>

                    {loading && (
                        <Box
                            sx={{
                                position: 'fixed',
                                bottom: '90px',
                                right: '20px',
                                backgroundColor: '#fff',
                                padding: '10px 20px',
                                borderRadius: '8px',
                                boxShadow: 3,
                            }}
                        >
                            <Typography variant="body2" color="primary">Fatura paylaştırılıyor...</Typography>
                        </Box>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        sx={{
                            position: 'fixed',
                            bottom: '20px',
                            right: '20px',
                        }}
                    >
                        Faturayı Paylaştır
                    </Button>

                    <Modal show={show} onHide={handleClose}>
                        <Modal.Header closeButton>
                            <Modal.Title>Faturayı Sil</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>Eksik sayaç okuma sonuçları mevcut. Devam etmek istediğinize emin misiniz? </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleClose}>
                                Hayır
                            </Button>
                            <Button variant="danger" onClick={handleModalConfirm}>
                                Evet
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Box>
            ) : (
                <Typography sx={{ padding: '20px' }} color="text.secondary">
                    Paylaştırılacak fatura bulunamadı
                </Typography>
            )}
        </div>
    );
};

export default AllocateInvoice;
